import { VariantProps, tv } from './utils/cn';
import { FCC } from './utils/fcc';

// Style Utilities

const textContentColors = tv({
  variants: {
    color: {
      primary: 'text-text-primary',
      secondary: 'text-text-secondary',
      tertiary: 'text-text-tertiary',
      supporting: 'text-text-supporting',
      disabled: 'text-text-disabled',
      inherit: 'text-inherit',
    },
  },
  defaultVariants: {
    color: 'primary',
  },
});

const headingStyles = tv({
  extend: textContentColors,
  variants: {
    responsive: { true: '', false: '' },
    size: {
      '5xl': 'text-5xl', // 36px
      '4xl': 'text-4xl', // 32px
      '3xl': 'text-3xl', // 28px
      '2xl': 'text-2xl', // 24px
      xl: 'text-xl', // 20px
      lg: 'text-lg', // 18px
    },
    weight: {
      bold: 'font-bold',
      semibold: 'font-semibold',
      medium: 'font-medium',
    },
  },
  compoundVariants: [
    { responsive: true, size: '5xl', className: 'text-4xl @md:text-5xl' },
    { responsive: true, size: '4xl', className: 'text-3xl @md:text-4xl' },
    { responsive: true, size: '3xl', className: 'text-2xl @md:text-3xl' },
    { responsive: true, size: '2xl', className: 'text-xl @md:text-2xl' },
    { responsive: true, size: 'xl', className: 'text-lg @md:text-xl' },
    { responsive: true, size: 'lg', className: 'text-md @md:text-lg' },
  ],
  defaultVariants: {
    responsive: false,
    size: '5xl',
    weight: 'medium',
  },
});

const textBodyStyles = tv({
  extend: textContentColors,
  variants: {
    responsive: { true: '', false: '' },
    size: {
      md: 'text-md', // 16px
      sm: 'text-sm', // 14px
      xs: 'text-xs', // 12px
    },
    weight: {
      bold: 'font-bold',
      semibold: 'font-semibold',
      medium: 'font-medium',
      regular: 'font-normal',
    },
  },
  compoundVariants: [
    { responsive: true, size: 'md', className: 'text-sm @md:text-md' },
    { responsive: true, size: 'sm', className: 'text-xs @md:text-sm' },
    { responsive: true, size: 'xs', className: 'text-2xs @md:text-xs' },
  ],
  defaultVariants: {
    responsive: false,
    size: 'sm',
    weight: 'medium',
  },
});

const captionStyles = tv({
  extend: textContentColors,
  base: 'uppercase',
  variants: {
    size: {
      '2xs': 'text-2xs', // 11px
      '3xs': 'text-3xs', // 10px
    },
    weight: {
      bold: 'font-bold',
      semibold: 'font-semibold',
    },
  },
  defaultVariants: {
    weight: 'semibold',
    size: '2xs',
  },
});

// Heading

type HeadingLevel = 1 | 2 | 3 | 4 | 5 | 6;

type HeadingSize = NonNullable<VariantProps<typeof headingStyles>['size']>;

type HeadingTag = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

/** Maps each numeric heading level to its HTML heading tag. */
const HeadingTagsByLevel: Record<HeadingLevel, HeadingTag> = {
  1: 'h1',
  2: 'h2',
  3: 'h3',
  4: 'h4',
  5: 'h5',
  6: 'h6',
};

/** Maps each heading size to its default HTML heading tag. */
const HeadingTagsBySize: Record<HeadingSize, HeadingTag> = {
  '5xl': 'h1',
  '4xl': 'h2',
  '3xl': 'h3',
  '2xl': 'h4',
  xl: 'h5',
  lg: 'h6',
};

/**
 * Props for the {@link Heading} component.
 *
 * Extends the style variant props (size, weight, color, responsive) with
 * an optional `level` override for the HTML heading tag.
 */
export type HeadingProps = VariantProps<typeof headingStyles> & {
  className?: string;
  /** The color of the heading. @default 'primary' */
  color?: VariantProps<typeof headingStyles>['color'];
  /** Overrides the HTML heading tag. When `'auto'`, derived from `size`. @default 'auto' */
  level?: HeadingLevel | 'auto';
  /** When true, steps down one size below the `@md` container breakpoint. @default false */
  responsive?: boolean;
  /** The font size of the heading. Also determines the HTML tag when `level` is `'auto'`. @default '5xl' */
  size?: VariantProps<typeof headingStyles>['size'];
  /** The font weight of the heading. @default 'medium' */
  weight?: VariantProps<typeof headingStyles>['weight'];
  [key: `data-${string}`]: string;
};

/**
 * Renders a heading element (`h1`–`h6`) with standardized sizing and styling.
 *
 * By default the HTML tag is derived from the `size` prop (`5xl` → `h1`,
 * `4xl` → `h2`, etc.). Use the `level` prop to override this when the
 * visual size doesn't match the document hierarchy.
 *
 * @example
 * <Heading size="3xl" level={2}>Visually large h2</Heading>
 */
export const Heading: FCC<HeadingProps> = ({
  children,
  className,
  color,
  level = 'auto',
  responsive,
  size = '5xl',
  weight,
  ...props
}) => {
  const Component =
    level === 'auto' ? HeadingTagsBySize[size] : HeadingTagsByLevel[level];

  return (
    <Component
      className={headingStyles({ className, color, responsive, size, weight })}
      {...props}
    >
      {children}
    </Component>
  );
};

// Text

/**
 * Props for the {@link Text} component.
 *
 * Extends the style variant props (size, weight, color, responsive) with
 * an `inline` toggle that controls whether the element renders as a `<p>`
 * (block, default) or `<span>` (inline).
 */
export type TextProps = VariantProps<typeof textBodyStyles> & {
  className?: string;
  /** The color of the text. @default 'primary' */
  color?: VariantProps<typeof textBodyStyles>['color'];
  /** Renders a `<span>` instead of a `<p>` when true. @default false */
  inline?: boolean;
  /** When true, steps down one size below the `@md` container breakpoint. @default false */
  responsive?: boolean;
  /** The font size of the text. @default 'sm' */
  size?: VariantProps<typeof textBodyStyles>['size'];
  /** The font weight of the text. @default 'medium' */
  weight?: VariantProps<typeof textBodyStyles>['weight'];
  [key: `data-${string}`]: string;
};

/**
 * Renders body text as a `<p>` (default) or `<span>` when `inline` is set.
 *
 * Use this for paragraphs, descriptions, labels, and general body copy.
 * Set `inline` when nesting inside other text elements, buttons, or
 * anywhere a block-level `<p>` would be invalid HTML.
 *
 * @example
 * <Text inline weight="bold">Inline emphasis</Text>
 */
export const Text: FCC<TextProps> = ({
  children,
  className,
  color,
  inline = false,
  responsive,
  size,
  weight,
  ...props
}) => {
  const Component = inline ? 'span' : 'p';

  return (
    <Component
      className={textBodyStyles({ className, color, responsive, size, weight })}
      {...props}
    >
      {children}
    </Component>
  );
};

// Caption

/**
 * Props for the {@link Caption} component.
 *
 * Extends the style variant props (size, weight, color) with an `inline`
 * toggle. Caption text is always uppercase.
 */
export type CaptionProps = VariantProps<typeof captionStyles> & {
  className?: string;
  /** The color of the caption. @default 'primary' */
  color?: VariantProps<typeof captionStyles>['color'];
  /** Renders a `<span>` instead of a `<p>` when true. @default false */
  inline?: boolean;
  /** The font size of the caption. @default '2xs' */
  size?: VariantProps<typeof captionStyles>['size'];
  /** The font weight of the caption. @default 'semibold' */
  weight?: VariantProps<typeof captionStyles>['weight'];
  [key: `data-${string}`]: string;
};

/**
 * Renders small uppercase text as a `<p>` (default) or `<span>` when
 * `inline` is set.
 *
 * Use this for labels, tags, section headers, and other small
 * auxiliary text that should appear in uppercase.
 *
 * @example
 * <Caption inline size="3xs">Tag</Caption>
 */
export const Caption: FCC<CaptionProps> = ({
  children,
  className,
  color,
  inline = false,
  size,
  weight,
  ...props
}) => {
  const Component = inline ? 'span' : 'p';

  return (
    <Component
      className={captionStyles({ className, color, size, weight })}
      {...props}
    >
      {children}
    </Component>
  );
};
